import React from 'react'
import {
  getWeek,
  getWeekYear,
  startOfWeek,
  endOfWeek,
  formatCompleteDate,
} from '../../../utils/date'
import { isSameWeek } from 'date-fns'
import { fr } from 'date-fns/locale'
import { WeekRowData, CellValue } from './types'
import { PayrollWorkerWithWeeksSummary } from '../../../hooks/payroll'
import { WorkPeriodEvent } from '../../../api/work-period-events'
import { UserSummaries } from '../../../api/missions'
import { getDocIdAsString } from '../../../utils/documents'
import { buildWorkPeriodEventLabel } from '../../../utils/work-period-events'
import { Element } from 'react-bulma-components'
import ReactTooltip from 'react-tooltip'
import { formatDuration, formatDurationAsDecimal, localDate } from '../../../utils/date'
import { getRuleValuesFromSummary } from '../../../utils/employer-rules'
import { CountersTooltip } from '../counters-tooltip'
import { calculateDayTotal } from '../../users/forms/user-form/contractual-schedules-utils'
import { getActiveContractualSchedule, getDayNameFromDate } from '../../../utils/users'

// Type pour un workPeriod depuis UserWeekSummary
type WorkPeriodFromSummary = UserSummaries['weeks'][0]['missions'][0]['workPeriods'][0]

//TODO: too many code repetitionn => refactor with helpers
//TODO: row builder return a mix of React.ReactNode and string/number/boolean/Date => use a proper type / approach

/**
 * Calcule les heures pour une date donnée en utilisant le contractual schedule ou les heures théoriques du workPeriod
 * @param date - Date concernée
 * @param workPeriod - WorkPeriod correspondant à cette date (depuis UserWeekSummary)
 * @param association - Association du worker (contient les contractual schedules)
 * @returns Nombre d'heures en millisecondes
 */
const getHoursForDate = (
  date: Date,
  workPeriod: WorkPeriodFromSummary,
  association: { contractualSchedules?: any[] } | undefined,
): number => {
  // Essayer d'obtenir le contractual schedule actif pour cette date
  if (association?.contractualSchedules) {
    const activeSchedule = getActiveContractualSchedule(association as any, date)

    if (activeSchedule?.schedule) {
      const dayName = getDayNameFromDate(date)
      const daySchedule = activeSchedule.schedule[dayName]

      // Si le schedule a des time slots pour ce jour, calculer les heures
      if (daySchedule?.timeSlots && daySchedule.timeSlots.length > 0) {
        const hoursInDay = calculateDayTotal(daySchedule.timeSlots)
        // Convertir les heures en millisecondes (heures * 60 * 60 * 1000)
        return hoursInDay * 60 * 60 * 1000
      }
    }
  }

  // Fallback : utiliser les heures théoriques du workPeriod
  return workPeriod.summary?.durations?.worked.theoretical || 0
}

/**
 * Helper pour créer une CellValue avec gestion des valeurs par défaut
 */
const createCellValue = (
  value: string | undefined,
  decimal?: string | undefined,
  isNA: boolean = false,
  color?: CellValue['color'],
): CellValue => ({
  value: value || '---',
  decimal: decimal || undefined,
  isNA,
  color,
})

/**
 * Helper pour calculer et formater une différence de durée (comme DurationDiff)
 */
const createDurationDiffCellValue = (
  duration: number | undefined,
  showZero: boolean = false,
  durationFormatter: (duration: number) => string = formatDuration,
  decimalFormatter: (duration: number) => string = formatDurationAsDecimal,
): CellValue => {
  const diff = duration || 0
  let color: CellValue['color'] = undefined
  let formattedValue = showZero && !diff ? '0' : diff ? durationFormatter(diff) : ''
  let formattedDecimal = showZero && !diff ? '0' : diff ? decimalFormatter(diff) : undefined

  if (diff > 0.1) {
    // TODO: use a proper value => 0.1 is a temporary value
    formattedValue = `+${formattedValue}`
    formattedDecimal = `+${formattedDecimal}`
    color = 'warning'
  } else if (diff < 0) {
    color = 'danger'
  }

  return createCellValue(formattedValue, formattedDecimal, undefined, color)
}

/**
 * Crée la structure de base commune pour WeekRowData
 */
const createBaseWeekRowData = (
  week: Date,
  worker: PayrollWorkerWithWeeksSummary,
  employerRules: UserSummaries['weeks'][0]['employerRules'],
  events: WorkPeriodEvent[],
): WeekRowData => {
  const weekNumber = getWeek(week)
  const year = getWeekYear(week)

  return {
    weekNumber: `S${weekNumber}`,
    workerId: worker._id,
    year: `${year}`,
    weekNumberValue: weekNumber,
    yearValue: year,
    weekStart: localDate(week),
    weekStartDate: startOfWeek(week),
    weekEndDate: endOfWeek(week),
    weekEnd: localDate(new Date(week.getTime() + 6 * 24 * 60 * 60 * 1000)),

    // Nouvelles structures CellValue
    contractHours: createCellValue('---'),
    worked: createCellValue('---'),
    nonEffective: createCellValue('---'),
    accounted: createCellValue('---', undefined, true), // N/A par défaut
    sentToCounters: createCellValue('---'),
    contractDiff: createCellValue('---'),
    contractDiffDecimal: createCellValue('---'),
    // Composants React complexes
    informativeEvents: '---',

    // États
    isRecorded: false,
    isValidated: false,
    isTotalRow: false,

    // Employer rules avec valeurs par défaut
    ...employerRules.reduce(
      (acc, rule) => {
        acc[`rule_${rule.ruleId}`] = '---'
        acc[`rule_${rule.ruleId}_decimal`] = '---'
        return acc
      },
      {} as Record<string, string>,
    ),
    // Benefit events avec valeurs par défaut
    ...events
      .filter(e => e.type === 'benefit')
      .reduce(
        (acc, event) => {
          acc[`event_${event.code}`] = '---'
          return acc
        },
        {} as Record<string, string>,
      ),
  }
}

/**
 * Calcule les données d'une ligne de semaine
 */
export const buildWeekRowData = (
  week: Date,
  worker: PayrollWorkerWithWeeksSummary,
  events: WorkPeriodEvent[],
  employerRules: UserSummaries['weeks'][0]['employerRules'],
): WeekRowData => {
  // Trouver le résumé de semaine correspondant
  const weekSummary = worker.weeksSummary?.weeks.find(
    w =>
      w.missions[0]?.workPeriods[0] &&
      isSameWeek(week, new Date(w.missions[0]?.workPeriods[0].start.date), {
        locale: fr,
        weekStartsOn: 1,
      }),
  )

  // Structure de base commune
  const baseData = createBaseWeekRowData(week, worker, employerRules, events)

  // Si pas de weekSummary, retourner avec les valeurs par défaut
  if (!weekSummary) {
    return baseData
  }

  // Récupérer les valeurs des employer rules pour cette semaine (formatées et décimales)
  const employerRulesValues =
    weekSummary?.totals && employerRules
      ? getRuleValuesFromSummary(employerRules, weekSummary.totals)
      : {}
  const employerRulesDecimalValues =
    weekSummary?.totals && employerRules
      ? getRuleValuesFromSummary(employerRules, weekSummary.totals, formatDurationAsDecimal)
      : {}

  // Récupérer les benefit events pour cette semaine
  const benefitEvents = events
    .filter(e => e.type === 'benefit')
    .reduce<{ [key: string]: React.ReactNode }>((eventsAcc, event) => {
      // Collecter les dates et workPeriods qui contiennent cet événement
      const eventData: Array<{ date: Date; workPeriod: WorkPeriodFromSummary }> = []
      const eventCount =
        weekSummary?.missions.reduce((totalCount, mission) => {
          return (
            totalCount +
            mission.workPeriods.reduce((missionCount, workPeriod) => {
              const matchingEvents = workPeriod.events.filter(
                we => getDocIdAsString(we.item) === getDocIdAsString(event),
              )

              // Si cet événement est présent dans ce workPeriod, ajouter la date et le workPeriod
              if (matchingEvents.length > 0) {
                eventData.push({
                  date: new Date(workPeriod.start.date),
                  workPeriod: workPeriod,
                })
              }

              return missionCount + matchingEvents.length
            }, 0)
          )
        }, 0) || 0

      // Construire le tooltip avec le label de l'événement et les dates concernées avec leurs heures
      const tooltipLabel = buildWorkPeriodEventLabel(event, true)
      const sortedEventData = eventData.sort((a, b) => a.date.getTime() - b.date.getTime())
      const benefitTooltipId = `benefit-${event.code}-${worker._id}-${week.getTime()}`

      eventsAcc[`event_${event.code}`] =
        eventCount > 0 ? (
          <>
            <Element data-tip data-for={benefitTooltipId}>
              {eventCount}
            </Element>
            <ReactTooltip id={benefitTooltipId} effect="solid" place="bottom">
              {tooltipLabel}
              {sortedEventData.map(({ date, workPeriod }) => (
                <React.Fragment key={date.getTime()}>
                  <br />
                  {formatCompleteDate(date)} (
                  {formatDuration(getHoursForDate(date, workPeriod, worker.association))})
                </React.Fragment>
              ))}
            </ReactTooltip>
          </>
        ) : (
          ''
        )
      return eventsAcc
    }, {})

  // Récupérer les informative events pour cette semaine (count + tooltip détaillé date/durée)
  /** Occurrences d’events informatifs avec date et workPeriod pour le tooltip (date + durée par jour) */
  const informativeEventData: Array<{
    date: Date
    workPeriod: WorkPeriodFromSummary
    event: WorkPeriodEvent
  }> = []

  weekSummary?.missions.forEach(mission =>
    mission.workPeriods.forEach(workPeriod => {
      workPeriod.events.forEach(we => {
        const eventItem = we.item as WorkPeriodEvent
        if (eventItem.type !== 'informative') return
        informativeEventData.push({
          date: new Date(workPeriod.start.date),
          workPeriod,
          event: eventItem,
        })
      })
    }),
  )

  const informativeCount = informativeEventData.length
  /** Regroupe par event (même doc) : label une fois + liste des dates/durées en dessous */
  const informativeByEvent = informativeEventData.reduce<
    Map<
      string,
      {
        event: WorkPeriodEvent
        occurrences: Array<{ date: Date; workPeriod: WorkPeriodFromSummary }>
      }
    >
  >((acc, { date, workPeriod, event }) => {
    const id = getDocIdAsString(event)
    const existing = acc.get(id)
    const occ = { date, workPeriod }
    if (existing) {
      existing.occurrences.push(occ)
    } else {
      acc.set(id, { event, occurrences: [occ] })
    }
    return acc
  }, new Map())
  // Trier les occurrences de chaque groupe par date
  informativeByEvent.forEach(group => {
    group.occurrences.sort((a, b) => a.date.getTime() - b.date.getTime())
  })
  const informativeTooltip: React.ReactNode =
    informativeByEvent.size > 0 ? (
      <>
        {Array.from(informativeByEvent.entries()).map(
          ([eventId, { event, occurrences }], groupIndex, entries) => (
            <React.Fragment key={eventId}>
              {buildWorkPeriodEventLabel(event, true)}
              {occurrences.length > 1 ? ` (x${occurrences.length})` : ''}
              {occurrences.map(({ date, workPeriod }) => (
                <React.Fragment key={date.getTime()}>
                  <br />
                  {formatCompleteDate(date)} (
                  {formatDuration(getHoursForDate(date, workPeriod, worker.association))})
                </React.Fragment>
              ))}
              {groupIndex < entries.length - 1 && <br />}
            </React.Fragment>
          ),
        )}
      </>
    ) : (
      ''
    )

  // Construire le tooltip détaillé pour les compteurs (si disponible dans employerData)
  // templateNamesMap permet d'afficher le nom du template au lieu de l'_id quand l'API ne renvoie pas workerCounter.template
  const countersTooltip: React.ReactNode | undefined = (() => {
    const counters = weekSummary?.totals?.employerData?.counters
    if (!counters || typeof counters !== 'object') return undefined

    const templateNamesMap =
      worker.workerCounters?.reduce<Record<string, string>>((acc, wc) => {
        const id = getDocIdAsString(wc.template)
        if (id && wc.template?.name) acc[id] = wc.template.name
        return acc
      }, {}) ?? {}

    return <CountersTooltip counters={counters} templateNamesMap={templateNamesMap} />
  })()

  return {
    ...baseData,
    weekSummary,

    // Nouvelles structures CellValue
    contractHours: createCellValue(
      weekSummary?.totals.durations.contract.total
        ? formatDuration(weekSummary.totals.durations.contract.total)
        : '00h00',
    ),
    worked: createCellValue(
      weekSummary?.totals.durations.worked.total
        ? formatDuration(weekSummary.totals.durations.worked.total)
        : '0h00',
      weekSummary?.totals.durations.worked.total
        ? formatDurationAsDecimal(weekSummary.totals.durations.worked.total)
        : '0',
    ),
    nonEffective: createCellValue(
      weekSummary?.totals.durations.benefit
        ? formatDuration(weekSummary.totals.durations.benefit)
        : '00h00',
      weekSummary?.totals.durations.benefit
        ? formatDurationAsDecimal(weekSummary.totals.durations.benefit)
        : '0',
    ),
    accounted: createCellValue(
      !weekSummary?.weekIsRecorded
        ? 'N/A'
        : formatDuration(weekSummary.totals.durations.accounted || 0),
      weekSummary?.weekIsRecorded
        ? formatDurationAsDecimal(weekSummary.totals.durations.accounted || 0)
        : undefined,
      !weekSummary?.weekIsRecorded,
    ),
    sentToCounters: createCellValue(
      weekSummary?.totals.durations.counters
        ? formatDuration(
            weekSummary.totals.durations.counters.totalSendToCountersFromAccountedCounters,
          )
        : undefined,
      weekSummary?.totals.durations.counters
        ? formatDurationAsDecimal(
            weekSummary.totals.durations.counters.totalSendToCountersFromAccountedCounters,
          )
        : undefined,
    ),

    // Structures CellValue pour les écarts (on affiche uniquement si l'écart est négatif)
    contractDiff:
      weekSummary?.totals?.durations?.worked?.contract?.diff < 0
        ? createDurationDiffCellValue(
            weekSummary?.totals?.durations?.worked?.contract?.diff || 0.1,
            true, // showZero
          )
        : createDurationDiffCellValue(0.1, true),
    contractDiffDecimal: createDurationDiffCellValue(
      weekSummary?.totals?.durations?.worked?.contract?.diff || 0.1,
      true, // showZero
      formatDurationAsDecimal,
      formatDurationAsDecimal,
    ),
    accountedHoursDecimal:
      weekSummary?.weekIsRecorded &&
      formatDurationAsDecimal(weekSummary.totals.durations.accounted || 0),
    accountedSentToCounters: weekSummary?.totals.durations.counters
      ? formatDuration(
          weekSummary.totals.durations.counters.totalSendToCountersFromAccountedCounters,
        )
      : undefined,
    accountedSentToCountersDecimal: weekSummary?.totals.durations.counters
      ? formatDurationAsDecimal(
          weekSummary.totals.durations.counters.totalSendToCountersFromAccountedCounters,
        )
      : undefined,
    // Tooltip détaillant la répartition par compteur
    accountedSentToCountersTooltip: countersTooltip,
    isRecorded: weekSummary?.weekIsRecorded || false,
    isValidated: weekSummary?.weekIsValidated || false,
    informativeEvents:
      informativeCount > 0 ? (
        <>
          <Element data-tip data-for={`informative-${worker._id}-${week.getTime()}`}>
            {informativeCount}
          </Element>
          <ReactTooltip
            id={`informative-${worker._id}-${week.getTime()}`}
            effect="solid"
            place="bottom"
          >
            {informativeTooltip}
          </ReactTooltip>
        </>
      ) : (
        ''
      ),
    // Override les employer rules avec les vraies valeurs
    ...employerRulesValues,
    ...Object.keys(employerRulesDecimalValues).reduce(
      (acc, key) => {
        acc[`${key}_decimal`] = employerRulesDecimalValues[key] || ''
        return acc
      },
      {} as Record<string, string | number>,
    ),
    // Override les benefit events avec les vraies valeurs
    ...benefitEvents,
  } as WeekRowData
}

/**
 * Crée la ligne de totaux pour le tableau
 */
export const createTotalRow = (
  data: WeekRowData[],
  worker: PayrollWorkerWithWeeksSummary,
  totalEmployerRulesValues: Record<string, string | number>,
  totalEmployerRulesDecimalValues: Record<string, string | number>,
  totalBenefitEvents: Record<string, number>,
  totalInformativeEvents: number,
  totalInformativeEventsByEvent: Record<string, number>,
  events: WorkPeriodEvent[],
  negativeContractDiff: number, // Temp => backend does not calculate this total yet => will be removed when backend is updated
): WeekRowData => {
  const informativeEventsTotalNode: React.ReactNode =
    totalInformativeEvents > 0
      ? (() => {
          const tooltipId = `informative-total-${worker._id}`
          const breakdownEntries = events
            .filter(e => e.type === 'informative')
            .map(e => ({
              event: e,
              count: totalInformativeEventsByEvent[getDocIdAsString(e)] || 0,
            }))
            .filter(({ count }) => count > 0)
          const tooltipContent = (
            <>
              {breakdownEntries.map(({ event, count }, i) => (
                <React.Fragment key={getDocIdAsString(event)}>
                  {i > 0 && <br />}
                  {buildWorkPeriodEventLabel(event, true)} x{count}
                </React.Fragment>
              ))}
            </>
          )
          return (
            <>
              <Element data-tip data-for={tooltipId}>
                <strong>{totalInformativeEvents}</strong>
              </Element>
              <ReactTooltip id={tooltipId} effect="solid" place="bottom">
                {tooltipContent}
              </ReactTooltip>
            </>
          )
        })()
      : ''

  return {
    weekNumber: 'TOTAL', // Valeur spéciale pour identifier la ligne de totaux
    year: '',
    weekStart: data[0]?.weekStart || '',
    weekEnd: data[data.length - 1]?.weekEnd || '',
    weekStartDate: data[0]?.weekStartDate,
    weekEndDate: data[data.length - 1]?.weekEndDate,
    workerId: '', // Pas de workerId pour la ligne de totaux

    // Nouvelles structures CellValue pour les totaux
    contractHours: createCellValue(
      worker.weeksSummary?.totals.durations.contract.total
        ? formatDuration(worker.weeksSummary.totals.durations.contract.total)
        : '0h',
    ),
    worked: createCellValue(
      worker.weeksSummary?.totals.durations.worked.total
        ? formatDuration(worker.weeksSummary.totals.durations.worked.total)
        : '0h',
      worker.weeksSummary?.totals.durations.worked.total
        ? formatDurationAsDecimal(worker.weeksSummary.totals.durations.worked.total)
        : '0',
    ),
    nonEffective: createCellValue(
      worker.weeksSummary?.totals.durations.benefit
        ? formatDuration(worker.weeksSummary.totals.durations.benefit)
        : '0h',
      worker.weeksSummary?.totals.durations.benefit
        ? formatDurationAsDecimal(worker.weeksSummary.totals.durations.benefit)
        : '0',
    ),
    accounted: createCellValue(
      worker.weeksSummary?.totals.durations.accounted >= 0
        ? formatDuration(worker.weeksSummary.totals.durations.accounted)
        : undefined,
      worker.weeksSummary?.totals.durations.accounted >= 0
        ? formatDurationAsDecimal(worker.weeksSummary.totals.durations.accounted)
        : undefined,
    ),
    sentToCounters: createCellValue(
      worker.weeksSummary?.totals.durations.counters
        ? formatDuration(
            worker.weeksSummary.totals.durations.counters.totalSendToCountersFromAccountedCounters,
          )
        : undefined,
      worker.weeksSummary?.totals.durations.counters
        ? formatDurationAsDecimal(
            worker.weeksSummary.totals.durations.counters.totalSendToCountersFromAccountedCounters,
          )
        : undefined,
    ),

    // Structures CellValue pour les écarts
    contractDiff: createDurationDiffCellValue(negativeContractDiff || 0.1),
    contractDiffDecimal: createDurationDiffCellValue(
      negativeContractDiff || 0.1,
      false, // showZero
      formatDurationAsDecimal,
      formatDurationAsDecimal,
    ),
    isRecorded: false,
    isValidated: false,
    isTotalRow: true,
    informativeEvents: informativeEventsTotalNode,
    // Utiliser directement les valeurs déjà formatées par getRuleValuesFromSummary
    ...totalEmployerRulesValues,
    ...Object.keys(totalEmployerRulesDecimalValues).reduce(
      (acc, key) => {
        acc[`${key}_decimal`] = totalEmployerRulesDecimalValues[key] || ''
        return acc
      },
      {} as Record<string, string | number>,
    ),
    ...Object.keys(totalBenefitEvents).reduce(
      (acc, key) => {
        acc[key] =
          totalBenefitEvents[key] && totalBenefitEvents[key] > 0 ? (
            <Element>
              <strong>{totalBenefitEvents[key]}</strong>
            </Element>
          ) : (
            ''
          )
        return acc
      },
      {} as Record<string, React.ReactNode>,
    ),
  } as WeekRowData
}
